import dayjs from 'dayjs';
import parse from 'html-react-parser';
import dynamic from 'next/dynamic';

import Link from '@components/atoms/link';
import { DEFAULT_BLOG_IMAGE } from '@lib/constants';
import type { CardLabel, HomepageTabColour } from '@lib/homepage/tabs';
import type { ListingItem } from '@lib/listings/types';

const Skeleton = dynamic(() => import('react-loading-skeleton'));

const DEFAULT_CATEGORY_LABEL = 'Fantasy Premier League';
const FALLBACK_AVATAR = '/images/hub-logo.svg';
const DATE_FORMAT = 'MMM D';
const SKELETON_LINES = { image: 7, category: 1, title: 4, meta: 2 };
// Half the card below lg; one grid column (max-w-7xl) from xl.
const CARD_IMAGE_SIZES = '(min-width: 1280px) 410px, 50vw';

export const LABEL_COLOUR_CLASSES: Record<HomepageTabColour, string> = {
  blue: 'text-primary-700',
  magenta: 'text-secondary-600',
  green: 'text-success-600',
  amber: 'text-warning-600'
};
const DEFAULT_LABEL_COLOUR: HomepageTabColour = 'blue';

type ListingCardProps = {
  /** Undefined renders the loading skeleton. */
  item?: ListingItem;
  /** The strand the card sits in; the CMS category name stands in when absent. */
  label?: CardLabel;
  onArticleClick?: () => void;
  className?: string;
};

export const formatListingDate = (publishedAt: string): string =>
  dayjs(publishedAt).format(DATE_FORMAT);

/**
 * One card in a listing grid, fed by either CMS through `ListingItem`. Below
 * `lg` it lies flat, image beside text; from `lg` it stands as a bordered
 * tile. The image carries its own link and the title link stretches over the
 * rest of the card; the author link sits above it so all three stay clickable.
 * The image link is hidden from assistive tech so the card reads as one link.
 */
export const ListingCard = ({
  item,
  label,
  onArticleClick,
  className = ''
}: ListingCardProps) => {
  const labelText = label?.text ?? item?.categoryName ?? DEFAULT_CATEGORY_LABEL;
  const labelColourClass =
    LABEL_COLOUR_CLASSES[label?.colour ?? DEFAULT_LABEL_COLOUR];

  return (
    <article
      className={`${className} relative flex flex-row gap-3 overflow-hidden bg-white transition-[transform,box-shadow] duration-150 ease-in-out lg:flex-col lg:gap-0 lg:rounded-[12px] lg:border lg:border-neutral-200 lg:shadow lg:hover:-translate-y-0.5 lg:hover:shadow-500`}
      data-cy="listing-card"
      data-source={item?.source}
    >
      {item ? (
        <div className="w-1/2 shrink-0 lg:w-full">
          <Link
            href={item.href}
            className="block"
            aria-hidden="true"
            tabIndex={-1}
            data-cy="listing-card-image-link"
            onClick={onArticleClick}
          >
            <img
              className="aspect-[16/10] w-full rounded-md object-cover lg:rounded-none"
              src={item.image?.url ?? DEFAULT_BLOG_IMAGE}
              srcSet={item.image?.srcSet}
              sizes={item.image?.srcSet ? CARD_IMAGE_SIZES : undefined}
              width={item.image?.width}
              height={item.image?.height}
              alt={item.image?.alt ?? ''}
              loading="lazy"
              decoding="async"
            />
          </Link>
        </div>
      ) : (
        <Skeleton count={SKELETON_LINES.image} />
      )}
      <div className="flex min-w-0 flex-1 flex-col py-0.5 pr-1 lg:px-5 lg:py-[18px]">
        {item ? (
          <span
            className={`font-open-sans text-xs font-bold uppercase tracking-[.06em] ${labelColourClass}`}
          >
            {labelText}
          </span>
        ) : (
          <Skeleton count={SKELETON_LINES.category} />
        )}
        {item ? (
          <h3 className="mt-1.5 font-sans text-sm font-semibold leading-5 text-neutral-800 [text-wrap:pretty] lg:mt-2 lg:text-[17px] lg:leading-[25px]">
            <Link
              href={item.href}
              className="font-sans font-semibold after:absolute after:inset-0 after:content-['']"
              data-cy="article-preview-link"
              onClick={onArticleClick}
            >
              {parse(item.title)}
            </Link>
          </h3>
        ) : (
          <Skeleton count={SKELETON_LINES.title} />
        )}
        {item ? (
          <div className="mt-auto flex items-center gap-[10px] pt-3 lg:pt-[18px]">
            <img
              className="hidden h-7 w-7 shrink-0 rounded-full bg-neutral-300 object-cover sm:block"
              src={item.author?.avatarUrl ?? FALLBACK_AVATAR}
              alt=""
            />
            {item.author && (
              <Link
                href={item.author.href}
                className="relative z-10 truncate font-open-sans text-[13px] font-semibold text-neutral-800 hover:underline"
              >
                {item.author.name}
              </Link>
            )}
            <span className="ml-auto whitespace-nowrap font-open-sans text-xs text-neutral-600">
              {item.publishedAt && (
                <time dateTime={dayjs(item.publishedAt).format('YYYY-MM-DD')}>
                  {formatListingDate(item.publishedAt)}
                </time>
              )}
              {item.publishedAt && item.readMinutes && (
                <span aria-hidden="true"> &middot; </span>
              )}
              {item.readMinutes && <span>{`${item.readMinutes} min`}</span>}
            </span>
          </div>
        ) : (
          <Skeleton count={SKELETON_LINES.meta} />
        )}
      </div>
    </article>
  );
};
